import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, capitalize, Chip, CircularProgress, Divider, Grid, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "material-react-table";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import CustomButton from "../Common/CustomButton";
import EllipsisedCell, { formatNumberAndCurrency } from "../Common/EllipsisedCell";
import FiltersBars from "../Common/FiltersBars";
import { LoggedInSkeleton } from "../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../Common/TopNavBar";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import ReactTable from "../ReactTable";
import { downloadExcelFromBuffer } from "../Vendor/UploadFileCommonDialog";
import { datefilterFn } from "../Vendor/UploadInvoice";
import { CN_Status_Type } from "../VendorNew/VendorSideDashboard";
import AnchorDashboardFilter from "./AnchorDashboardFilter";
import BulkOperationsModal from "./BulkOperationModal";

export const convertHighlightToDataByTab = (highlight: Highlight) => {
  return [
    {
      title: "Invoice paid early",
      value: formatNumberAndCurrency(highlight.earlyPaidInvoiceCount, false),
    },
    {
      title: "Value of invoice paid early",
      value: formatNumberAndCurrency(highlight.earlyPaidInvoiceValue, true),
    },
    {
      title: "Total disbursed amount",
      value: formatNumberAndCurrency(highlight.totalDisbursedAmount, true),
    },
    {
      title: "Income accrued",
      value: formatNumberAndCurrency(highlight.incomeAccrued, true),
    },
    {
      title: "Total discount amount",
      value: formatNumberAndCurrency(highlight.totalDiscountAmount, true),
    },
  ];
};

export type AllowedFilterKeysAnchorPayments =
  | "vendorName"
  | "panNumber"
  | "grnNumber"
  | "invoiceNumber"
  | "earlyPayDate"
  | "cnStatus"
  | "dnAdjusted";

export enum IYesNo {
  YES = "Yes",
  NO = "No",
}
export enum EarlyPayPass {
  YES = "Yes",
  NO = "No",
}

export enum InitiationDayFilter {
  TODAY = "today",
  YESTERDAY = "yesterday",
  ALL = "all",
}

export enum DiscountingStatus {
  FULLY_DISCOUNTED = "Fully Discounted",
  PARTIALLY_DISCOUNTED = "Partially Discounted",
  NOT_DISCOUNTED = "Not Discounted",
}

export enum CNAgeingStatus {
  CN_DUE = "CN due",
  CN_NOT_DUE = "CN not due",
}
export interface IEarlyPayment {
  initiationDate: Date;
  earlyPayDate: Date;
  adjustedDiscountRates: number;
  finalAdjustments: number;
  holdOffAmount: number;
  outstandingAmount: number;
  startingCB: number;
  effectiveCB: number;
  grossPaymentAmount: number;
  amountDisbursed: number;
  balanceAmountToBePaidOnDueDate: number;
  discountAmount: number;
  creditNoteNumber: string;
  earlyPayPass: IYesNo;
  discountingStatus: DiscountingStatus;
  incomeAccruedToSwiggy: number;
  incomeAccruedToNAKAD: number;
  cnAgeing: number;
  cnAgeingStatus: CNAgeingStatus;
  outstandingAmountNakad: number;
  cnStatus: CN_Status_Type;
  cnNumber: string;
  isCreditNote: boolean;
  vendorCnDate: Date;
  vendorCnNumber: string;
}

export type ReducedInvoice = Omit<Invoice, "actorMapping" | "earlyPaymentData">;

export interface Highlight {
  earlyPaidInvoiceCount: number;
  earlyPaidInvoiceValue: number;
  totalDisbursedAmount: number;
  incomeAccrued: number;
  totalDiscountAmount: number;
  currentAUM?: number;
}

export interface EarlyPaymentData extends ReducedInvoice, IEarlyPayment {
  useInvoiceCreditPeriod: boolean;
  vendorName: string;
}

export interface EarlyPaymentResponse {
  invoices: EarlyPaymentData[];
  highlights: Highlight;
}

export enum InvoicePostedStatus {
  POSTED = "Posted",
  NOT_POSTED = "Not Posted",
}

export enum OverDueStatus {
  DUE = "Due",
  OVER_DUE = "Overdue",
  NOT_DUE = "Not Due",
}

export enum PaymentStatus {
  PAID = "Paid",
  PARTIALLY_PAID = "Partially Paid",
  UNPAID = "Unpaid",
}

export enum NakadDueStatus {
  DUE = "Due",
  NOT_DUE = "Not Due",
}

export enum EarlyPaymentStatus {
  UNDER_PROCESSING = "Under processing",
  YET_TO_BE_INITIATED = "Yet to be initiated",
  PROCESSED = "Processed",
}

export interface Invoice {
  id: string;
  entityName: string;
  vendorCode: string;
  pan: string;
  gstin: string;
  warehouseName: string;
  city: string;
  state: string;
  invoicePostedStatus: InvoicePostedStatus;
  poNumber: string;
  poDate: Date;
  poAmount: number;
  grnNumber: string;
  grnDate: Date;
  grnAmount: number;
  creatorInvoiceDate: Date;
  invoiceNumber: string;
  invoiceAccountDate: Date;
  invoiceAmount: number;
  tds: number;
  purchaseReturnAmount: number;
  brandDiscount: number;
  debitAmount: number;
  adjustments: number;
  netPayableAmount: number;
  otherAdjustmentDate: Date;
  invoiceReconciledStatus: string;
  paymentAmount: number;
  paymentReferenceNumber: number;
  outstandingAmount: number;
  creditPeriod: number;
  dueDate: Date;
  newDueDate: Date;
  overDueStatus: OverDueStatus;
  paymentStatus: PaymentStatus;
  lastPaymentDate: Date;
  nakadDueStatus: NakadDueStatus;
  earlyPaymentData: IEarlyPayment | null;
  anchorCnReceived: IYesNo;
  nakadCnReceived: IYesNo;
  earlyPaymentStatus: EarlyPaymentStatus;
  dnAdjusted: IYesNo;
  isFreezed: boolean;
  earlyPayDate: Date;
  createdAt: Date;
  updatedAt: Date;
  isDeleted: boolean;
  deletedAt?: Date;
}

export const StatCard = ({ title, value }: { title: string; value: string | number }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 1,
      border: "1px solid #E5E5E5",
      background: "linear-gradient(180deg, #FFF 0%, #F5F5F5 100%)",
      boxShadow: "0 0 8px 4px rgba(0, 0, 0, 0.02)",
    }}
  >
    <Typography fontSize={14} color="text.secondary" fontWeight={500}>
      {title}
    </Typography>
    <Typography fontSize={22} fontWeight={600} mt={1}>
      {value}
    </Typography>
  </Box>
);

const AnchorDashboard = () => {
  const { actor } = useContext(userContext);
  const location = useLocation<any>();
  const [selectedTime, setSelectedTime] = useState<InitiationDayFilter>(InitiationDayFilter.ALL);
  const [openBulkDialog, setOpenBulkDialog] = useState<boolean>(false);
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<EarlyPaymentData[]>([]);
  const [clearRows, setClearRows] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<AllowedFilterKeysAnchorPayments, string[]>>({
    vendorName: [],
    panNumber: [],
    grnNumber: [],
    invoiceNumber: [],
    earlyPayDate: [],
    cnStatus: [],
    dnAdjusted: [],
  });
  const [allData, setAllData] = useState<EarlyPaymentData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [highlights, setHighlights] = useState<Highlight>({
    earlyPaidInvoiceCount: 0,
    earlyPaidInvoiceValue: 0,
    totalDisbursedAmount: 0,
    totalDiscountAmount: 0,
    incomeAccrued: 0,
    currentAUM: 0,
  });
  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false);
  const [isLoadingPerformaCN, setIsLoadingPerformaCN] = useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  const handleDownloadReport = async () => {
    setIsDownloadingReport(true);
    const startDate = appliedFilters.earlyPayDate[0]
      ? moment(appliedFilters.earlyPayDate[0]).format("DD-MM-YYYY")
      : null;
    const endDate = appliedFilters.earlyPayDate[1] ? moment(appliedFilters.earlyPayDate[1]).format("DD-MM-YYYY") : null;
    await useFetch("/invoices/previous-payment/2/report", "POST", {
      data: {
        vendorName: appliedFilters.vendorName || [],
        pan: appliedFilters.panNumber || [],
        grnNumber: appliedFilters.grnNumber || [],
        invoiceNumber: appliedFilters.invoiceNumber || [],
        earlyPayStartDate: startDate,
        earlyPayEndDate: endDate,
        initiationDay: selectedTime,
        cnStatus: appliedFilters.cnStatus || [],
        dnAdjusted: appliedFilters.dnAdjusted || [],
      },
      failureMessage: "Failed to download report",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.previousPaymentsReport, "Payment report.xlsx");
        setIsDownloadingReport(false);
      },
      catchCallBack: () => {
        setIsDownloadingReport(false);
      },
    });
  };

  const handleTimeFilter = (label: InitiationDayFilter) => {
    setSelectedTime(label);
  };

  useEffect(() => {
    getAllData();
  }, [appliedFilters, selectedTime]);

  const handleDownloadPerformaCN = async (invoiceId: string) => {
    setIsLoadingPerformaCN(true);
    await useFetch(`/invoices/${invoiceId}/credit-note-performa/download`, "GET", {
      failureMessage: "Failed to download Performa CN",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.creditNoteTemplate, "report.xlsx");
        setIsLoadingPerformaCN(false);
      },
      catchCallBack: () => {
        setIsLoadingPerformaCN(false);
      },
    });
  };

  const getAllData = async () => {
    const startDate = appliedFilters.earlyPayDate[0]
      ? moment(appliedFilters.earlyPayDate[0]).format("DD-MM-YYYY")
      : null;
    const endDate = appliedFilters.earlyPayDate[1] ? moment(appliedFilters.earlyPayDate[1]).format("DD-MM-YYYY") : null;
    setIsLoading(true);
    useFetch("/invoices/previous-payment/2", "POST", {
      failureMessage: "Failed to fetch data",
      data: {
        vendorName: appliedFilters.vendorName || [],
        pan: appliedFilters.panNumber || [],
        grnNumber: appliedFilters.grnNumber || [],
        invoiceNumber: appliedFilters.invoiceNumber || [],
        earlyPayStartDate: startDate,
        earlyPayEndDate: endDate,
        initiationDay: selectedTime,
        cnStatus: appliedFilters.cnStatus || [],
        dnAdjusted: appliedFilters.dnAdjusted || [],
      },
      thenCallBack: (response) => {
        setAllData(response.data.result.invoices || []);
        setHighlights(response.data.result.highlights);
        setIsLoading(false);
      },
      catchCallBack: () => {
        setIsLoading(false);
      },
    });
  };

  const handleDownloadCN = async (invoiceId: string) => {
    await useFetch(`/invoices/credit-note/download/${invoiceId}`, "GET", {
      failureMessage: "Failed to download Credit Note",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.creditNoteFile, response.data.result.fileName);
      },
    });
  };

  const dataByTab = convertHighlightToDataByTab(highlights);
  const cardCount = dataByTab.length;
  const mdSize = cardCount >= 5 ? 12 / 5 : 12 / cardCount;

  const columns = useMemo<MRT_ColumnDef<EarlyPaymentData>[]>(
    () => [
      {
        header: "Vendor Name",
        accessorKey: "vendorName",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
        minSize: 250,
      },
      {
        header: "PAN",
        accessorKey: "pan",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
        minSize: 150,
      },
      {
        header: "GRN Number",
        accessorKey: "grnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
        minSize: 250,
      },
      {
        header: "GRN Date",
        accessorKey: "grnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.grnDate, filterValue);
        },
        accessorFn: (row) => (row.grnDate ? new Date(row.grnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Invoice Number",
        accessorKey: "invoiceNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
        minSize: 250,
      },
      {
        header: "Payment to be made",
        accessorKey: "amountDisbursed",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
        minSize: 250,
        filterVariant: "range",
      },
      {
        header: "Discount Amount",
        accessorKey: "discountAmount",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
        minSize: 250,
        filterVariant: "range",
      },
      {
        header: "Initiation Date",
        accessorKey: "initiationDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.initiationDate, filterValue);
        },
        accessorFn: (row) => (row.initiationDate ? new Date(row.initiationDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },

      {
        header: "Vendor CN Date",
        accessorKey: "vendorCnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.vendorCnDate, filterValue);
        },
        accessorFn: (row) => (row.vendorCnDate ? new Date(row.vendorCnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },

      {
        header: "Vendor CN Number",
        accessorKey: "vendorCnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GRN Amount",
        accessorKey: "grnAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Early Payment Status",
        accessorKey: "earlyPaymentStatus",
        filterVariant: "multi-select",
        minSize: 280,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "DN adjusted",
        accessorKey: "dnAdjusted",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Income accrued to Swiggy",
        accessorKey: "incomeAccruedToSwiggy",
        size: 300,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "CN status",
        accessorKey: "cnStatus",
        size: 200,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "CN Number",
        accessorKey: "cnNumber",
        size: 200,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Organization Name",
        accessorKey: "entityName",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice amount",
        accessorKey: "invoiceAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Actual CN received (Swiggy)",
        accessorKey: "anchorCnReceived",
        minSize: 300,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },

      {
        header: "Early pay date",
        accessorKey: "earlyPayDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.earlyPayDate, filterValue);
        },
        accessorFn: (row) => (row.earlyPayDate ? new Date(row.earlyPayDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Vendor Code",
        accessorKey: "vendorCode",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Actual CN received (NAKAD)",
        accessorKey: "nakadCnReceived",
        minSize: 300,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "CN ageing status",
        accessorKey: "cnAgeingStatus",
        minSize: 200,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GSTIN",
        accessorKey: "gstin",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Warehouse Name",
        accessorKey: "warehouseName",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "City",
        accessorKey: "city",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "State",
        accessorKey: "state",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Status of Invoice",
        accessorKey: "invoicePostedStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "PO No.",
        accessorKey: "poNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "PO Date",
        accessorKey: "poDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.poDate, filterValue);
        },
        accessorFn: (row) => (row.poDate ? new Date(row.poDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "PO Amount",
        accessorKey: "poAmount",
        filterVariant: "range",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Creator Invoice Date",
        accessorKey: "creatorInvoiceDate",
        size: 250,
        accessorFn: (row) => (row.creatorInvoiceDate ? moment(row.creatorInvoiceDate).format("DD/MM/YYYY") : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice Accounting Date",
        accessorKey: "invoiceAccountDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.invoiceAccountDate, filterValue);
        },
        accessorFn: (row) => (row.invoiceAccountDate ? new Date(row.invoiceAccountDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "TDS/TCS",
        accessorKey: "tds",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Purchase Return Amount",
        accessorKey: "purchaseReturnAmount",
        size: 300,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Brand discount (Promo Claims)",
        accessorKey: "brandDiscount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other Debit Amount",
        accessorKey: "debitAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other adjustments",
        accessorKey: "adjustments",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Net Payable Amount",
        accessorKey: "netPayableAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other Adjustment Date",
        accessorKey: "otherAdjustmentDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.otherAdjustmentDate, filterValue);
        },
        accessorFn: (row) => (row.otherAdjustmentDate ? new Date(row.otherAdjustmentDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Invoice Status",
        accessorKey: "invoiceReconciledStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Payment amount",
        accessorKey: "paymentAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Payment Reference No",
        accessorKey: "paymentReferenceNumber",
        size: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Outstanding payment",
        accessorKey: "outstandingAmount",
        filterVariant: "range",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Credit Period",
        accessorKey: "creditPeriod",
        filterVariant: "range",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="number" />,
      },
      {
        header: "Due Date",
        accessorKey: "dueDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.dueDate, filterValue);
        },
        accessorFn: (row) => (row.dueDate ? new Date(row.dueDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "New due date",
        accessorKey: "newDueDate",
        filterVariant: "date-range",
        size: 350,
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.newDueDate, filterValue);
        },
        accessorFn: (row) => (row.newDueDate ? new Date(row.newDueDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Overdue",
        accessorKey: "overDueStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Payment status",
        accessorKey: "paymentStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<PaymentStatus>()} type="text" />,
      },
      {
        header: "Last Payment Date",
        accessorKey: "lastPaymentDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.lastPaymentDate, filterValue);
        },
        accessorFn: (row) => (row.lastPaymentDate ? new Date(row.lastPaymentDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Proforma CN",
        id: "proformaCN",
        size: 150,
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              justifyContent: "center",
              width: "100%",
            }}
          >
            <CustomButton
              variant="outlined"
              size="small"
              disabled={isLoadingPerformaCN}
              onClick={() => handleDownloadPerformaCN(row.original.id)}
            >
              Download
            </CustomButton>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Upload", actor.name, location?.state?.openCollapseOb, actor)}
    >
      <Box sx={{ bgcolor: "#fff", p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1}>
          <Chip
            label={"Current AUM: " + formatNumberAndCurrency(highlights.currentAUM, true)}
            sx={{
              color: "#0288D1",
              backgroundColor: "#E3F2FD",
              borderRadius: "4px",
              fontWeight: 600,
              fontSize: "1rem",
            }}
          />
          <CustomButton
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => {
              setOpenFilter(true);
            }}
          >
            Filter
          </CustomButton>
        </Stack>

        <Grid container spacing={2} mb={4}>
          {dataByTab.map(({ title, value }, index) => (
            <Grid key={index} item xs={12} sm={6} md={mdSize}>
              <StatCard title={title} value={value} />
            </Grid>
          ))}
        </Grid>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Account Overview
          </Typography>
          <Stack direction="row" spacing={2}>
            <CustomButton
              variant="contained"
              endIcon={<ArrowDropDownIcon />}
              onClick={() => setOpenBulkDialog(true)}
              disabled={selectedRow.length === 0}
            >
              Bulk Actions
            </CustomButton>
            <CustomButton
              variant="outlined"
              onClick={handleDownloadReport}
              disabled={isDownloadingReport}
              startIcon={isDownloadingReport ? <CircularProgress size={20} /> : null}
            >
              Download Report
            </CustomButton>
          </Stack>
        </Box>
        <Divider />
        <FiltersBars
          appliedFilters={appliedFilters}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          setAppliedFilters={setAppliedFilters}
        />

        <Stack direction="row" spacing={1} p={2}>
          {Object.values(InitiationDayFilter).map((label) => (
            <Chip
              key={label}
              label={capitalize(label)}
              variant={selectedTime === label ? "filled" : "outlined"}
              color="default" // keep this neutral, since we're overriding styles
              onClick={() => handleTimeFilter(label)}
              sx={{
                fontWeight: 500,
                backgroundColor: selectedTime === label ? "#EEE8ED" : "transparent",
                color: selectedTime === label ? "#541C4C" : "inherit",
                borderColor: selectedTime === label ? "#EEE8ED" : "#ccc", // optional for outlined
              }}
            />
          ))}
        </Stack>
        <ReactTable
          data={allData}
          columns={columns}
          isLoading={isLoading}
          enableRowSelection={true}
          setSelectedRow={setSelectedRow}
          clearRowsSelection={clearRows}
          enableRowActions={true}
          enableRowVirtualization={true}
          enableColumnVirtualization={true}
          onColumnFiltersChange={setColumnFilters}
          additionalState={{
            columnFilters,
          }}
          additionalInitialState={{
            columnPinning: {
              left: ["mrt-row-select"],
              right: ["mrt-row-actions"],
            },
            columnVisibility: {
              invoicePostedStatus: false,
              tds: false,
              purchaseReturnAmount: false,
              brandDiscount: false,
              debitAmount: false,
              adjustments: false,
              netPayableAmount: false,
              otherAdjustmentDate: false,
              invoiceReconciledStatus: false,
              paymentAmount: false,
              paymentReferenceNumber: false,
              outstandingAmount: false,
              creditPeriod: false,
              newDueDate: false,
              overDueStatus: false,
              paymentStatus: false,
              lastPaymentDate: false,
            },
          }}
          displayColumnDefOptions={{
            "mrt-row-actions": {
              header: "Credit Note",
              size: 150,
            },
          }}
          renderRowActions={({ row }) => (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                justifyContent: "center",
                width: "100%",
              }}
            >
              <CustomButton
                variant="outlined"
                size="small"
                disabled={!row.original.isCreditNote}
                onClick={() => {
                  handleDownloadCN(row.original.id);
                }}
              >
                Download
              </CustomButton>
            </Box>
          )}
        />
      </Box>
      {openFilter && (
        <AnchorDashboardFilter
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
          useCase={"anchorSidePayments"}
        />
      )}
      {openBulkDialog && (
        <BulkOperationsModal
          open={openBulkDialog}
          onClose={() => setOpenBulkDialog(false)}
          selectedRowIds={selectedRow.map((row) => row.id)}
          clearSelectedRows={() => setClearRows(true)}
          useCase="anchor"
          getAllData={getAllData}
        />
      )}
    </LoggedInSkeleton>
  );
};

export default AnchorDashboard;
